import React, { useState } from 'react';
import { Drawer, Input, Select, Switch, Button } from 'antd';
import { STATE_OPTIONS, CHANNEL_OPTIONS, FREQUENCY_OPTIONS } from './segment/operators';
import { buildSummary } from './segment/segmentHumanize';
import { buildProductById } from './segment/segmentRead';
import SegmentSummary from './segment/SegmentSummary';
import SegmentEditorModal from './segment/SegmentEditorModal';
import JourneyPermissionGate from './JourneyPermissionGate';
import { PERMISSION_JOURNEYS_CREATE, PERMISSION_JOURNEYS_UPDATE, PERMISSION_JOURNEYS_DELETE } from '../../auth/permissions';
import './timeline.css';

// Controlled presentational drawer. All state/behavior comes from `editor`
// (the useJourneyEditor return). The page owns open/close + save.
export default function JourneyEditorDrawer({ open, editor, onRequestClose, onSave, onDelete = () => {}, attributeById }) {
  const [segOpen, setSegOpen] = useState(false);
  if (!editor) return null;
  const { values: form, set, isCreate, canSave, saving, segmentDraft, setSegmentDraft, labelUuids, setLabelUuids, labelOptions, productOptions } = editor;
  const setInput = (k) => (e) => set(k)(e.target.value);
  const submitPermission = isCreate ? PERMISSION_JOURNEYS_CREATE : PERMISSION_JOURNEYS_UPDATE;
  // Resolve product names for the summary from the already-loaded productOptions, using the
  // same shared helper the journey card uses — so card and drawer name products identically.
  const summaryModel = buildSummary(segmentDraft.groups, segmentDraft.conditionOperator, { attributeById, productById: buildProductById(productOptions) });

  return (
    <Drawer
      title={isCreate ? 'Add journey' : 'Configure journey'}
      placement="right"
      width={460}
      mask={false}
      visible={open}
      onClose={onRequestClose}
      footer={
        <div className="tp-footer">
          <div className="tp-footer-left">
            {/* Delete lives here (not on the panel) and is permission-gated. Only an existing
                journey can be deleted — hidden while creating one. */}
            {!isCreate && (
              <JourneyPermissionGate permission={PERMISSION_JOURNEYS_DELETE}>
                <Button danger type="text" aria-label="delete journey" onClick={onDelete}>Delete</Button>
              </JourneyPermissionGate>
            )}
          </div>
          <div className="tp-footer-right">
            <Button onClick={onRequestClose}>Cancel</Button>
            <JourneyPermissionGate permission={submitPermission}><Button type="primary" loading={saving} disabled={!canSave} onClick={onSave}>
              {isCreate ? 'Create journey' : 'Save changes'}
            </Button></JourneyPermissionGate>
          </div>
        </div>
      }
    >
      <div className="tp-grp">
        <div className="tp-grp-h">Content</div>
        <label className="tp-lbl" htmlFor="j-name">Name</label>
        <JourneyPermissionGate permission={PERMISSION_JOURNEYS_UPDATE}><Input id="j-name" aria-label="Journey name" value={form.name} onChange={setInput('name')} /></JourneyPermissionGate>
        <label className="tp-lbl">State</label>
        <JourneyPermissionGate permission={PERMISSION_JOURNEYS_UPDATE}><select aria-label="Journey state" value={form.state} onChange={(e) => set('state')(e.target.value)}>
          {STATE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select></JourneyPermissionGate>
      </div>

      <div className="tp-grp">
        <div className="tp-grp-h">Delivery &amp; order</div>
        <label className="tp-lbl">Channels</label>
        <JourneyPermissionGate permission={PERMISSION_JOURNEYS_UPDATE}><Select mode="multiple" aria-label="Channels" style={{ width: '100%' }} value={form.channels} onChange={set('channels')} options={CHANNEL_OPTIONS} /></JourneyPermissionGate>
        <div className="tp-lbl tp-toggle"><span>Restartable</span><JourneyPermissionGate permission={PERMISSION_JOURNEYS_UPDATE}><Switch aria-label="Restartable" checked={form.isRestartable} onChange={set('isRestartable')} /></JourneyPermissionGate></div>
        <div className="tp-lbl tp-toggle"><span>Recurring</span><JourneyPermissionGate permission={PERMISSION_JOURNEYS_UPDATE}><Switch aria-label="Recurring" checked={form.isRecurring} onChange={set('isRecurring')} /></JourneyPermissionGate></div>
        {form.isRecurring && (
          <>
            <label className="tp-lbl">Frequency</label>
            <JourneyPermissionGate permission={PERMISSION_JOURNEYS_UPDATE}><select aria-label="Recurrence frequency" value={form.frequency} onChange={(e) => set('frequency')(e.target.value)}>
              <option value="">Select…</option>
              {FREQUENCY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select></JourneyPermissionGate>
            <label className="tp-lbl">Start date</label>
            <JourneyPermissionGate permission={PERMISSION_JOURNEYS_UPDATE}><Input type="date" aria-label="Recurrence start date" value={form.startDate} onChange={setInput('startDate')} /></JourneyPermissionGate>
            <label className="tp-lbl">Time (UTC)</label>
            <JourneyPermissionGate permission={PERMISSION_JOURNEYS_UPDATE}><Input type="time" aria-label="Recurrence time" value={form.time} onChange={setInput('time')} /></JourneyPermissionGate>
          </>
        )}
      </div>

      <div className="tp-grp">
        <div className="tp-grp-h">Segmentation</div>
        <div className="seg-preview-card"><SegmentSummary model={summaryModel} /></div>
        <JourneyPermissionGate permission={PERMISSION_JOURNEYS_UPDATE}><button type="button" className="tp-add seg-edit-btn" onClick={() => setSegOpen(true)}>Edit segmentation</button></JourneyPermissionGate>
      </div>

      <div className="tp-grp">
        <div className="tp-grp-h">Labels</div>
        <JourneyPermissionGate permission={PERMISSION_JOURNEYS_UPDATE}><Select mode="multiple" aria-label="Journey labels" style={{ width: '100%' }} value={labelUuids} onChange={setLabelUuids} optionFilterProp="label" options={labelOptions} /></JourneyPermissionGate>
      </div>

      <SegmentEditorModal
        open={segOpen}
        draft={segmentDraft}
        productOptions={productOptions}
        attributeById={attributeById}
        onCancel={() => setSegOpen(false)}
        onDone={(next) => { setSegmentDraft(next); setSegOpen(false); }}
      />
    </Drawer>
  );
}
